import React, { FC, useState, useEffect, useCallback } from 'react';
import { TextField, Typography, IconButton } from '@material-ui/core';
import { useMutation } from '@apollo/react-hooks';
import ImageKit from 'imagekit-javascript';
import { useDropzone } from 'react-dropzone';

import { translate } from '../../../../utils/helpers';
import { CustomButton } from '../../../../components/UI/CustomButton';
import { IKImage } from '../../../../components/IKImage';
import CloseIcon from '../../../../assets/icons/CloseIcon';
import { colorsPalette } from '../../../../theme';
import { UPDATE_COMPANY_INFO } from '../../../../graphql/user';
import { Mutation, MutationEditCompanyArgs } from '../../../../types';
import { multiLangCharRegexp } from '../../../../utils/regexps';
import { ALLOWED_MIME_TYPES } from '../../pages/const';
import useStyles from './styles';

interface OrganizationFormProps {
  companyShortName?: string | null;
  companyLogo?: string | null;
  setFormEnabled: Function;
  setOpenAlert: Function;
}

const OrganizationForm: FC<OrganizationFormProps> = ({
  companyShortName,
  companyLogo,
  setFormEnabled,
  setOpenAlert,
}) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [companyLogoName, setCompanyLogoName] = useState('');

  const [shortName, setShortName] = useState(companyShortName || '');
  const [logo, setLogo] = useState<File | undefined>(undefined);
  const [logoName, setLogoName] = useState('');

  const [shortNameErr, setShortNameErr] = useState('');
  const [logoErr, setLogoErr] = useState('');

  const [touched, setTouched] = useState(false);

  const [updateCompanyInfo] = useMutation<Pick<Mutation, 'editCompany'>, MutationEditCompanyArgs>(
    UPDATE_COMPANY_INFO,
  );

  useEffect(() => {
    if (companyLogo) {
      const arr = companyLogo.split('/');
      setCompanyLogoName(arr.length > 1 ? arr[arr.length - 1] : companyLogo);
      setCompanyLogoUrl(companyLogo);
    }
  }, [companyLogo]);

  const validateShortName = useCallback((shortName: string): boolean => {
    const name = shortName.trim();

    if (!name) {
      setShortNameErr(translate('error.fieldRequired'));
      return false;
    } else if (name.length < 2) {
      setShortNameErr(translate('error.minCharCountErr', { count: 1 }));
      return false;
    } else if (name.length > 12) {
      setShortNameErr(translate('error.maxCharCountErr', { count: 12 }));
      return false;
    } else if (!name.match(multiLangCharRegexp)) {
      setShortNameErr(translate('error.atLeastOneCharacter'));
      return false;
    }

    setShortNameErr('');
    return true;
  }, []);

  const submitForm = async (logoUrl?: string) => {
    await updateCompanyInfo({
      variables: {
        input: {
          short_name: shortName.trim(),
          logo_url: logoUrl || companyLogoUrl || null,
        },
      },
    });
    setLogoUrl('');
    setLogo(undefined);
    setLogoName('');
    setShortNameErr('');
    setTouched(false);
    setFormEnabled(false);
    setLoading(false);
    setOpenAlert(true);
  };

  const handleSubmitForm = async () => {
    if (!validateShortName(shortName) || logoErr) {
      setTouched(true);
    } else {
      setLoading(true);
      if (logo) {
        uploadFile(async (err, result) => {
          if (result) {
            await submitForm(result.filePath);
          }
        });
      } else {
        await submitForm();
      }
    }
  };

  const handleChangeImage = useCallback((acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (file) {
      setLogo(file);
      setLogoName(file.name);
      if (ALLOWED_MIME_TYPES.includes(file.type)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          if (reader.result) {
            const image = new Image();
            const imgUrl =
              typeof reader.result === 'string'
                ? reader.result
                : Buffer.from(reader.result).toString();
            setLogoUrl(imgUrl);
            image.src = imgUrl;
            image.onload = () => {
              if (image.width < 300 || image.height < 100) {
                setLogoErr(translate('error.minImageSize', { width: 300, height: 100 }));
              } else {
                setLogoErr('');
              }
            };
          }
        };
      } else {
        setLogoErr(translate('error.fileShouldBeImage'));
      }
    }
  }, []);

  const uploadFile = (callback) => {
    const imagekit = new ImageKit({
      publicKey: process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY,
      urlEndpoint: process.env.REACT_APP_IMAGEKIT_ENDPOINT_URL,
      authenticationEndpoint: `${process.env.REACT_APP_HTTP_API_URL}/api/imagekit/token`,
    });

    imagekit.upload(
      {
        file: logo,
        fileName: logo?.name,
        useUniqueFileName: true,
        folder: '/logos',
      },
      callback,
    );
  };

  const handleRemoveLogo = () => {
    setLogo(undefined);
    setLogoUrl('');
    setCompanyLogoUrl('');
    setLogoName('');
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop: handleChangeImage });

  return (
    <>
      <div className={classes.wrapper}>
        <form
          className={classes.form}
          onSubmit={async (e) => {
            e.preventDefault();
            await handleSubmitForm();
          }}
        >
          <TextField
            className={classes.input}
            name="display-org-name"
            label={translate('page.account.displayNamePlaceholder')}
            variant="outlined"
            value={shortName}
            onChange={(event) => {
              setShortName(event.target.value);
              if (touched) {
                validateShortName(event.target.value);
              }
            }}
            error={touched && !!shortNameErr}
            helperText={touched && shortNameErr}
          />
          <div className={classes.logoBlock}>
            <Typography className={classes.logoTitle}>
              {translate('page.account.organizationLogo')}
            </Typography>
            <div className={classes.logoWrapper}>
              {(logoUrl || companyLogoUrl) && (
                <div className={classes.logoRow}>
                  <div className={classes.logoInfo}>
                    {logoUrl ? (
                      <img className={classes.logo} alt="logo" src={logoUrl} />
                    ) : (
                      <IKImage
                        className={classes.logo}
                        path={companyLogo}
                        srcset={[{ width: 140 }]}
                        sizes="100vw"
                      />
                    )}
                    <Typography className={classes.logoName}>
                      {logoName ? logoName : companyLogoName}
                    </Typography>
                  </div>
                  <IconButton className={classes.removeButton} onClick={handleRemoveLogo}>
                    <CloseIcon className={classes.closeIcon} color={colorsPalette.gray} />
                  </IconButton>
                </div>
              )}
              {touched && logoErr && <p className={classes.logoErr}>{logoErr}</p>}
            </div>
            <div {...getRootProps()} className={classes.dropzone}>
              <input {...getInputProps()} name="image" accept="image/*" />
              <Typography className={classes.dropzoneText}>
                {translate('page.account.fileDropzoneText')}
              </Typography>
              <Typography className={classes.dropzoneText}>
                {translate('page.account.fileDropzoneRequirements')}
              </Typography>
              <CustomButton color="secondary" btnClass={classes.dropzoneButton}>
                {translate('button.browse')}
              </CustomButton>
            </div>
          </div>
          <CustomButton disabled={loading} type="submit">
            {translate('button.save')}
          </CustomButton>
        </form>
      </div>
    </>
  );
};

export default OrganizationForm;
